import { lazy } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';

import { SuspenseBoundary } from '../../components/ui/SuspenseBoundary';
import { ErrorFallback } from '../../components/ui/ErrorFallback';
import ProtectedRoute from '../../providers/ProtectedRoute';
import { Permission } from '../../types';

import { AppLayout } from './AppLayout';
import { HomePage } from './HomePage';

const Profile = lazy(() => import('../profile/Profile'));
const PaymentMethodsWithStripe = lazy(() => import('../profile/PaymentMethodsWithStripe'));
const Login = lazy(() => import('../auth/Login'));
const Register = lazy(() => import('../auth/Register'));
const NotFound = lazy(() => import('./NotFound'));
const ProjectDetail = lazy(() => import('../projects/[id]/[id].page'));
const AdminUsersPage = lazy(() => import('../admin/UsersPage'));
const AdminProjectsPage = lazy(() => import('../admin/AdminProjectsPage'));
const ProjectsPage = lazy(() => import('../projects/projects.page'));
const VerifierPoolPage = lazy(() => import('../verifier/VerifierPool.page'));
const VerifierProjectsPage = lazy(() => import('../verifier/VerifierProjects.page'));

/**
 * Router configuration for the application
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <ErrorFallback />,
    children: [
      // Login route
      {
        path: 'login',
        element: (
          <SuspenseBoundary>
            <Login />
          </SuspenseBoundary>
        ),
        errorElement: <ErrorFallback />,
      },
      // Register route
      {
        path: 'register',
        element: (
          <SuspenseBoundary>
            <Register />
          </SuspenseBoundary>
        ),
        errorElement: <ErrorFallback />,
      },
      {
        index: true,
        element: (
          <ProtectedRoute>
            <SuspenseBoundary>
              <HomePage />
            </SuspenseBoundary>
          </ProtectedRoute>
        ),
        errorElement: <ErrorFallback />,
      },
      {
        path: 'projects',
        element: (
          <ProtectedRoute requiredPermission={Permission.ACCESS_PROJECTS}>
            <SuspenseBoundary>
              <Outlet />
            </SuspenseBoundary>
          </ProtectedRoute>
        ),
        errorElement: <ErrorFallback />,
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute requiredPermission={Permission.VIEW_PROJECTS_LIST}>
                <ProjectsPage />
              </ProtectedRoute>
            ),
            errorElement: <ErrorFallback />,
          },
          {
            path: ':id',
            element: <ProjectDetail />,
            errorElement: <ErrorFallback />,
          },
        ],
      },
      {
        path: 'profile',
        element: (
          <ProtectedRoute>
            <SuspenseBoundary>
              <Profile />
            </SuspenseBoundary>
          </ProtectedRoute>
        ),
        errorElement: <ErrorFallback />,
      },
      {
        path: 'profile/payment-methods',
        element: (
          <ProtectedRoute>
            <SuspenseBoundary>
              <PaymentMethodsWithStripe />
            </SuspenseBoundary>
          </ProtectedRoute>
        ),
        errorElement: <ErrorFallback />,
      },
      {
        path: 'admin/users',
        element: (
          <ProtectedRoute requiredPermission={Permission.MANAGE_USERS}>
            <SuspenseBoundary>
              <AdminUsersPage />
            </SuspenseBoundary>
          </ProtectedRoute>
        ),
        errorElement: <ErrorFallback />,
      },
      {
        path: 'admin/projects',
        element: (
          <ProtectedRoute requiredPermission={Permission.VIEW_ALL_PROJECTS}>
            <SuspenseBoundary>
              <AdminProjectsPage />
            </SuspenseBoundary>
          </ProtectedRoute>
        ),
        errorElement: <ErrorFallback />,
      },
      {
        path: 'verifier',
        element: (
          <ProtectedRoute requiredPermission={Permission.ACCESS_VERIFIER_POOL}>
            <SuspenseBoundary />
          </ProtectedRoute>
        ),
        errorElement: <ErrorFallback />,
        children: [
          {
            path: 'pool',
            element: <VerifierPoolPage />,
            errorElement: <ErrorFallback />,
          },
          {
            path: 'projects',
            element: <VerifierProjectsPage />,
            errorElement: <ErrorFallback />,
          },
        ],
      },
    ],
  },
  // 404 - Not Found
  {
    path: '*',
    element: (
      <SuspenseBoundary>
        <NotFound />
      </SuspenseBoundary>
    ),
    errorElement: <ErrorFallback />,
  },
]);
